import { createFileRoute, notFound, redirect } from '@tanstack/react-router'
import { UserSearch } from 'lucide-react'
import { z } from 'zod'
import type { NotFoundPageData } from '@/pages/NotFoundPage.tsx'
import { fetchTalent } from '@/services/api/resources.ts'
import {
  ConsultantErrorFallback,
  ConsultantPage,
} from '@/pages/ConsultantPage.tsx'
import { resourcesKeys } from '@/services/api/resourcesHooks.ts'
import { HttpError } from '@/utils/apiUtils.ts'
import { requireRoles } from '@/utils/routeGuards.ts'
import { ROUTE_ACCESS } from '@/constants/routeAccess.ts'
import { ROUTES } from '@/constants/routeConstants'
import { TALENT_TABS } from '@/constants/talentTabsConstants.ts'

const CONSULTANT_NOT_FOUND_DATA = {
  icon: UserSearch,
  titleKey: 'consultants/list:errors.notFoundTitle',
  subtitleKey: 'consultants/list:errors.notFoundSubtitle',
  ctaLabelKey: 'consultants/list:errors.backToList',
  ctaTo: ROUTES.CONSULTANTS_LIST,
} satisfies NotFoundPageData

export const talentSearchSchema = z.object({
  tab: z.enum(TALENT_TABS).optional().catch(undefined),
})

export const Route = createFileRoute('/consultants/$consultantId')({
  beforeLoad: requireRoles(ROUTE_ACCESS[ROUTES.CONSULTANT_DETAIL]),
  validateSearch: talentSearchSchema,
  loader: async ({ params, context }) => {
    let talent
    try {
      talent = await context.queryClient.ensureQueryData({
        queryKey: resourcesKeys.talent(params.consultantId),
        queryFn: () => fetchTalent(params.consultantId),
        staleTime: 15 * 60 * 1000,
      })
    } catch (error) {
      if (error instanceof HttpError && error.status === 404) {
        throw notFound({ data: CONSULTANT_NOT_FOUND_DATA })
      }
      throw error
    }

    if (!talent.is_displayed_on_cleverlink) {
      throw notFound({ data: CONSULTANT_NOT_FOUND_DATA })
    }

    // Merged talent: the API returns the surviving talent, so align the URL
    if (talent.unified_identity_id !== params.consultantId) {
      context.queryClient.setQueryData(
        resourcesKeys.talent(talent.unified_identity_id),
        talent,
      )
      throw redirect({
        to: ROUTES.CONSULTANT_DETAIL,
        params: { consultantId: talent.unified_identity_id },
        replace: true,
      })
    }

    return talent
  },
  errorComponent: ConsultantErrorFallback,
  component: ConsultantPage,
})
